import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { userService, dealService } from '../../../services';
import { DealDTO, DealStatus, UserStatus, CreateDealRequest, CardDTO } from '../../../types';
import { ErrorDisplay, DropdownMenu, ProfileStatusModal, CreateDealModal, CardManagementModal, DealDetailsModal } from '../../../components';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Button, 
  Input, 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Chip,
  Avatar,
  Divider,
  Spinner,
  Badge,
  useDisclosure
} from '@heroui/react';

// SVG Icons
import { ReactComponent as CardIcon } from '../../../icons/card.svg';
import { ReactComponent as AvatarIcon } from '../../../icons/avatar.svg';
import { ReactComponent as AvatarVerifiedIcon } from '../../../icons/avatar_verified.svg';
import { ReactComponent as AvatarSelfIcon } from '../../../icons/avatar_self.svg';
import { ReactComponent as InfoIcon } from '../../../icons/info.svg';
import { ReactComponent as LogoutIcon } from '../../../icons/logout.svg';
import { ReactComponent as ShareIcon } from '../../../icons/share.svg';

const DesktopDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout, user, updateUser } = useAuth();
  const [deals, setDeals] = useState<DealDTO[]>([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [showCreateDealModal, setShowCreateDealModal] = useState(false);
  const [showProfileStatusModal, setShowProfileStatusModal] = useState(false);
  const [showCardManagementModal, setShowCardManagementModal] = useState(false);
  const [showDealDetailsModal, setShowDealDetailsModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<DealDTO | null>(null);
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());
  const [createDealForm, setCreateDealForm] = useState({
    amount: '',
    purpose: '',
    cardId: '',
    isFnsEnabled: false
  });
  const [createDealLoading, setCreateDealLoading] = useState(false);
  const [userCards, setUserCards] = useState<CardDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'active' | 'history'>('active');
  const [notification, setNotification] = useState<{ message: string; isVisible: boolean }>({ message: '', isVisible: false });

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await userService.getCurrentUser();
        updateUser(userData);
        
        // Карты пользователя уже загружены в user.cards
        if (userData.cards && userData.cards.length > 0) {
          setUserCards(userData.cards);
          setCreateDealForm(prev => ({ ...prev, cardId: userData.cards![0].id }));
        } else {
          setUserCards([]);
          setCreateDealForm(prev => ({ ...prev, cardId: '' }));
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
        setError('Не удалось загрузить данные пользователя');
      }
    };

    const loadDeals = async () => {
      try {
        setDealsLoading(true);
        setError(null);
        const dealsData = await dealService.getDeals();
        setDeals(dealsData);
      } catch (error) {
        console.error('Failed to load deals:', error);
        setError('Не удалось загрузить сделки');
      } finally {
        setDealsLoading(false);
      }
    };

    loadUserData();
    loadDeals();
  }, [updateUser]);

  // Обработка возврата пользователя с параметром rim
  useEffect(() => {
    const rimValue = searchParams.get('rim');
    if (rimValue) {
      const handleRimReturn = async () => {
        try {
          // Вызываем POST /user/rim/verify
          await userService.verifyRimScan();
          
          // Обновляем информацию о пользователе
          const updatedUser = await userService.getCurrentUser();
          updateUser(updatedUser);
          
          // Показываем уведомление об успехе
          showNotification('Паспорт успешно отсканирован! Статус обновлен.');
          
          // Убираем параметр rim из URL
          navigate('/dashboard', { replace: true });
          
        } catch (error: any) {
          console.error('Error verifying RIM scan:', error);
          if (error.response?.status === 400) {
            showNotification('Ошибка при верификации паспорта. Попробуйте еще раз.');
          } else {
            showNotification('Ошибка при верификации паспорта');
          }
          
          // Убираем параметр rim из URL даже при ошибке
          navigate('/dashboard', { replace: true });
        }
      };
      
      handleRimReturn();
    }
  }, [searchParams, updateUser, navigate]);

  // Функция для выхода из системы
  const handleLogout = () => {
    // Очищаем токены из localStorage
    userService.clearUserFromStorage();
    
    // Вызываем logout из useAuth
    logout();
    
    // Переходим на страницу логина
    navigate('/login');
  };

  // Функция для фильтрации сделок
  const getFilteredDeals = () => {
    if (activeFilter === 'active') {
      // Активные сделки: New, Registered, Wait, Paid
      return deals.filter(deal => 
        deal.status === DealStatus.New ||
        deal.status === DealStatus.Registered ||
        deal.status === DealStatus.Wait ||
        deal.status === DealStatus.Paid
      );
    } else {
      // История: Finished, Rejected, Refunded
      return deals.filter(deal => 
        deal.status === DealStatus.Finished ||
        deal.status === DealStatus.Rejected ||
        deal.status === DealStatus.Refunded
      );
    }
  };

  // Функция для показа уведомлений
  const showNotification = (message: string) => {
    setNotification({ message, isVisible: true });
    // Автоматически скрываем уведомление через 5 секунд
    setTimeout(() => {
      setNotification(prev => ({ ...prev, isVisible: false }));
    }, 5000);
  };

  const handleDealClick = (deal: DealDTO) => {
    setSelectedDeal(deal);
    setShowDealDetailsModal(true);
  };

  const handleCloseDealDetails = () => {
    setShowDealDetailsModal(false);
    setSelectedDeal(null);
  };

  const handleMenuAction = (actionId: string) => {
    switch (actionId) {
      case 'scanPassport':
        // TODO: Реализовать сканирование паспорта
        console.log('Сканирование паспорта');
        break;
      case 'passportByPhone':
        // TODO: Реализовать верификацию по номеру телефона
        console.log('Верификация по номеру телефона');
        break;
      case 'enableFns':
        // TODO: Реализовать подключение "Мой налог"
        console.log('Подключение "Мой налог"');
        break;
      case 'disableFns':
        // TODO: Реализовать отключение "Мой налог"
        console.log('Отключение "Мой налог"');
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  const handleCreateDeal = async (dealData?: { purpose: string; amount: number; cardId: string }) => {
    if (userCards.length === 0) {
      setError('Для создания сделки необходимо добавить банковскую карту');
      return;
    }
    
    // Если переданы данные от модала, используем их
    if (dealData) {
      if (!dealData.purpose || !dealData.amount) {
        setError('Пожалуйста, заполните все поля');
        return;
      }

      try {
        setCreateDealLoading(true);
        setError(null);
        
        const createDealRequest: CreateDealRequest = {
          purpose: dealData.purpose,
          amount: dealData.amount * 100, // Конвертируем в копейки
          cardId: dealData.cardId,
          isFnsEnabled: createDealForm.isFnsEnabled
        };

        const newDeal = await dealService.createDeal(createDealRequest);
        if (newDeal) {
          setDeals(prev => [newDeal, ...prev]);
          setShowCreateDealModal(false);
          showNotification('Сделка успешно создана');
        }
      } catch (error) {
        console.error('Failed to create deal:', error);
        setError('Ошибка при создании сделки');
      } finally {
        setCreateDealLoading(false);
      }
      return;
    }
    
    // Старая логика для совместимости
    if (!createDealForm.purpose || !createDealForm.amount || !createDealForm.cardId) {
      setError('Пожалуйста, заполните все поля');
      return;
    }

    try {
      setCreateDealLoading(true);
      setError(null);
      
      const createDealRequest: CreateDealRequest = {
        purpose: createDealForm.purpose,
        amount: parseInt(createDealForm.amount) * 100, // Конвертируем в копейки
        cardId: createDealForm.cardId,
        isFnsEnabled: createDealForm.isFnsEnabled
      };

      const newDeal = await dealService.createDeal(createDealRequest);
      if (newDeal) {
        setDeals(prev => [newDeal, ...prev]);
        setShowCreateDealModal(false);
        setCreateDealForm({ amount: '', purpose: '', cardId: createDealForm.cardId, isFnsEnabled: false });
        showNotification('Сделка успешно создана');
      }
    } catch (error) {
      console.error('Failed to create deal:', error);
      setError('Ошибка при создании сделки');
    } finally {
      setCreateDealLoading(false);
    }
  };

  const handleReplaceCard = async () => {
    try {
      const response = await userService.addCard();
      if (response.url) {
        window.location.href = response.url;
      } else {
        setError('Не удалось получить ссылку для замены карты');
      }
    } catch (error) {
      console.error('Failed to replace card:', error);
      setError('Ошибка при замене карты');
    }
  };

  const handleDeleteCard = async () => {
    if (userCards.length === 0) return;
    
    try {
      await userService.removeCard(userCards[0].id);
      setUserCards([]);
      setCreateDealForm(prev => ({ ...prev, cardId: '' }));
      setShowCardManagementModal(false);
    } catch (error) {
      console.error('Failed to delete card:', error);
      setError('Ошибка при удалении карты');
    }
  };

  const toggleDealExpansion = (dealId: string) => {
    const newExpanded = new Set(expandedDeals);
    if (newExpanded.has(dealId)) {
      newExpanded.delete(dealId);
    } else {
      newExpanded.add(dealId);
    }
    setExpandedDeals(newExpanded);
  };

  const copyPaymentLink = async (dealId: string) => {
    try {
      // Получаем ссылку для оплаты сделки
      const paymentResult = await dealService.payDeal(dealId, '');
      if (paymentResult?.url) {
        await navigator.clipboard.writeText(paymentResult.url);
        showNotification('Ссылка скопирована в буфер обмена');
      } else {
        setError('Не удалось получить ссылку для оплаты');
      }
    } catch (error) {
      console.error('Failed to get payment link:', error);
      setError('Ошибка при получении ссылки для оплаты');
    }
  };

  const deleteDeal = async (dealId: string) => {
    if (window.confirm('Вы уверены, что хотите удалить эту сделку?')) {
      try {
        const success = await dealService.deleteDeal(dealId);
        if (success) {
          setDeals(prev => prev.filter(deal => deal.id !== dealId));
          setExpandedDeals(prev => {
            const newSet = new Set(prev);
            newSet.delete(dealId);
            return newSet;
          });
          showNotification('Сделка успешно удалена');
        } else {
          setError('Не удалось удалить сделку');
        }
      } catch (error) {
        console.error('Failed to delete deal:', error);
        setError('Ошибка при удалении сделки');
      }
    }
  };

  const refundDeal = async (dealId: string) => {
    if (window.confirm('Вы уверены, что хотите сделать возврат по этой сделке?')) {
      try {
        const success = await dealService.refundDeal(dealId);
        if (success) {
          // Обновляем список сделок
          const updatedDeals = await dealService.getDeals();
          setDeals(updatedDeals);
          showNotification('Возврат по сделке успешно оформлен');
        } else {
          setError('Не удалось оформить возврат');
        }
      } catch (error) {
        console.error('Failed to refund deal:', error);
        setError('Ошибка при оформлении возврата');
      }
    }
  };

  const confirmDeal = async (dealId: string) => {
    if (window.confirm('Вы уверены, что хотите подтвердить сделку и произвести выплату на карту?')) {
      try {
        const success = await dealService.confirmDeal(dealId);
        if (success) {
          // Обновляем список сделок
          const updatedDeals = await dealService.getDeals();
          setDeals(updatedDeals);
          showNotification('Сделка подтверждена и выплата инициирована');
        } else {
          setError('Не удалось подтвердить сделку');
        }
      } catch (error) {
        console.error('Failed to confirm deal:', error);
        setError('Ошибка при подтверждении сделки');
      }
    }
  };

  const getStatusText = (status: DealStatus): string => {
    switch (status) {
      case DealStatus.New: return 'Создана';
      case DealStatus.Registered: return 'Зарегистрирована';
      case DealStatus.Paid: return 'Оплачена';
      case DealStatus.Finished: return 'Завершена';
      case DealStatus.Rejected: return 'Отклонена';
      case DealStatus.Refunded: return 'Возвращена';
      case DealStatus.Wait: return 'Ожидает';
      default: return 'Неизвестно';
    }
  };

  const getStatusColor = (status: DealStatus): "default" | "primary" | "secondary" | "success" | "warning" | "danger" => {
    switch (status) {
      case DealStatus.New:
      case DealStatus.Registered:
      case DealStatus.Wait:
        return "primary";
      case DealStatus.Paid:
        return "warning";
      case DealStatus.Finished:
        return "success";
      case DealStatus.Rejected:
      case DealStatus.Refunded:
        return "danger";
      default:
        return "default";
    }
  };

  const getUserStatusText = (status?: UserStatus): string => {
    switch (status) {
      case UserStatus.Anonymous: return 'Аноним';
      case UserStatus.Verified: return 'Верифицирован';
      case UserStatus.Selfemployed: return 'Самозанятый';
      default: return 'Неизвестно';
    }
  };

  const getUserStatusDescription = (status?: UserStatus): string => {
    switch (status) {
      case UserStatus.Anonymous: return 'Лимит 15 000 ₽ в месяц';
      case UserStatus.Verified: return 'Лимит 100 000 ₽ в месяц';
      case UserStatus.Selfemployed: return 'Без лимитов';
      default: return '';
    }
  };

  const getAvatarIcon = (status?: UserStatus) => {
    switch (status) {
      case UserStatus.Verified:
        return <AvatarVerifiedIcon className="w-6 h-6 text-white" />;
      case UserStatus.Selfemployed:
        return <AvatarSelfIcon className="w-6 h-6 text-white" />;
      default:
        return <AvatarIcon className="w-6 h-6 text-white" />;
    }
  };

  const formatAmount = (amount: number): string => {
    return (amount / 100).toLocaleString('ru-RU');
  };

  const formatCardNumber = (maskedCardNumber: string): string => {
    if (!maskedCardNumber) return '';
    
    // Убираем все звездочки и получаем только цифры
    const digits = maskedCardNumber.replace(/\*/g, '');
    
    if (digits.length < 8) return maskedCardNumber; // Если цифр меньше 8, возвращаем как есть
    
    // Берем первые 4 и последние 4 цифры
    const firstFour = digits.slice(0, 4);
    const lastFour = digits.slice(-4);
    
    return `${firstFour} **** **** ${lastFour}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    // Если дата сегодня, показываем только время
    if (diffInHours < 24 && date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    // Если дата вчера, показываем "вчера, время"
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `вчера, ${date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })}`;
    }
    
    // Иначе показываем дату и время в формате "15 авг., 12:45"
    const months = [
      'янв.', 'фев.', 'мар.', 'апр.', 'май', 'июн.',
      'июл.', 'авг.', 'сен.', 'окт.', 'ноя.', 'дек.'
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const time = date.toLocaleTimeString('ru-RU', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    return `${day} ${month}, ${time}`;
  };

  const handleAddCard = async () => {
    try {
      const response = await userService.addCard();
      
      if (response.url) {
        // Переходим на URL для добавления карты
        window.location.href = response.url;
      } else {
        setError('Не удалось получить ссылку для добавления карты');
      }
    } catch (error) {
      console.error('Failed to add card:', error);
      setError('Ошибка при добавлении карты');
    }
  };

  const handleRemoveCard = async (cardId: string) => {
    if (window.confirm('Вы уверены, что хотите удалить эту карту?')) {
      try {
        const success = await userService.removeCard(cardId);
        if (success) {
          // Обновляем список карт пользователя
          const updatedUserData = await userService.getCurrentUser();
          updateUser(updatedUserData);
          
          if (updatedUserData.cards && updatedUserData.cards.length > 0) {
            setUserCards(updatedUserData.cards);
            setCreateDealForm(prev => ({ ...prev, cardId: updatedUserData.cards![0].id }));
          } else {
            setUserCards([]);
            setCreateDealForm(prev => ({ ...prev, cardId: '' }));
          }
          
          showNotification('Карта успешно удалена');
        } else {
          setError('Не удалось удалить карту');
        }
      } catch (error) {
        console.error('Failed to remove card:', error);
        setError('Ошибка при удалении карты');
      }
    }
  };

  const getDealActions = (deal: DealDTO) => {
    const actions = [];
    
    if (deal.status === DealStatus.New || deal.status === DealStatus.Registered || deal.status === DealStatus.Wait) {
      actions.push(
        { id: 'copy', label: 'Копировать', type: 'primary' as const, loading: false },
        { id: 'delete', label: 'Удалить', type: 'secondary' as const, loading: false }
      );
    } else if (deal.status === DealStatus.Paid) {
      actions.push(
        { id: 'refund', label: 'Вернуть', type: 'secondary' as const, loading: false },
        { id: 'confirm', label: 'Подтвердить', type: 'primary' as const, loading: false }
      );
    }
    
    return actions;
  };

  const handleShareDeal = async (deal: DealDTO) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: deal.purpose,
          text: `Сделка "${deal.purpose}" на сумму ${formatAmount(deal.amount)} ₽`,
          url: `https://dealtochka.com/pay/${deal.id}`,
        });
      } catch (err) {
        // Игнорируем ошибки шаринга
      }
    }
  };

  const handleDealAction = async (actionId: string, dealId: string) => {
    switch (actionId) {
      case 'copy':
        await copyPaymentLink(dealId);
        break;
      case 'delete':
        await deleteDeal(dealId);
        break;
      case 'refund':
        await refundDeal(dealId);
        break;
      case 'confirm':
        await confirmDeal(dealId);
        break;
    }
  };

  return (
    <div className="min-h-screen bg-[#0f121c] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute w-full h-full">
        {/* Ellipse 5883 */}
        <div 
          className="absolute w-[400px] h-[200px] left-[-200px] top-[50px] bg-[rgba(23,12,90,0.3)] opacity-60 blur-[35.05px] rotate-[-30deg]"
          style={{
            background: 'rgba(23, 12, 90, 0.3)',
            opacity: 0.6,
            filter: 'blur(35.05px)',
            transform: 'rotate(-30deg)'
          }}
        />
        
        {/* Ellipse 5884 */}
        <div 
          className="absolute w-[350px] h-[300px] right-[100px] top-[200px] bg-[rgba(23,12,90,0.3)] opacity-60 blur-[35.05px] rotate-[-30deg]"
          style={{
            background: 'rgba(23, 12, 90, 0.3)',
            opacity: 0.6,
            filter: 'blur(35.05px)',
            transform: 'rotate(-30deg)'
          }}
        />
      </div>

      {/* Error Display */}
      {error && (
        <ErrorDisplay 
          error={error} 
          onClose={() => setError(null)} 
        />
      )}

      {/* Top Menu */}
      <div className="px-8 py-8 flex items-center justify-between relative z-10">
        {/* Avatar - leads to profile screen */}
        <div className="w-16 h-16 bg-[#7661d7] rounded-full flex items-center justify-center cursor-pointer">
          <span className="text-white text-2xl">A</span>
        </div>
        
        {/* Logout icon */}
        <div 
          className="w-16 h-16 flex items-center justify-center cursor-pointer hover:bg-white/10 rounded-full transition-colors"
          onClick={handleLogout}
        >
          <LogoutIcon className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Welcome Text */}
      <div className="px-8 mb-8 relative z-10">
        <div className="text-white/80 text-lg mb-2">Личный кабинет</div>
        <div className="text-white text-4xl">Добро пожаловать!</div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="px-8 pb-8 relative z-10">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left Column - User Info and Cards */}
          <div className="xl:col-span-1 space-y-6">
            {/* Bank Cards Panel */}
            <div 
              className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 cursor-pointer hover:bg-white/10 transition-colors"
              onClick={userCards.length === 0 ? handleAddCard : () => setShowCardManagementModal(true)}
            >
              <div className="flex flex-col items-start space-y-4">
                <div className="w-16 h-16 bg-[#2e3036] rounded-full flex items-center justify-center">
                  <CardIcon className="w-8 h-8 text-white" />
                </div>
                <div className="text-white text-xl">
                  {userCards.length > 0 
                    ? formatCardNumber(userCards[0].maskedCardNumber) || '**** 0000'
                    : '+ Добавить'
                  }
                </div>
                <div className="text-white/70 text-base">Банковская карта</div>
              </div>
            </div>

            {/* Profile Status Panel */}
            <div 
              className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 cursor-pointer hover:bg-white/10 transition-colors"
              onClick={() => setShowProfileStatusModal(true)}
            >
              <div className="flex flex-col items-start space-y-4">
                <div className="w-16 h-16 bg-[#2e3036] rounded-full flex items-center justify-center">
                  {getAvatarIcon(user?.status)}
                </div>
                <div className="text-white text-xl">{getUserStatusText(user?.status)}</div>
                <div className="text-white/70 text-base">Статус</div>
              </div>
            </div>

            {/* Information Panel */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <InfoIcon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 text-white text-base leading-relaxed">
                  {userCards.length > 0 
                    ? 'Создайте сделку, клиент/покупатель ее оплатит, и вы получите средства на банковскую карту'
                    : 'Для создания сделки необходимо добавить банковскую карту'
                  }
                </div>
              </div>
            </div>

            {/* Create Deal Button */}
            <button 
              className={`w-full py-5 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 text-lg ${
                userCards.length === 0
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-white text-gray-800 hover:bg-white/90 active:scale-95'
              }`}
              onClick={() => {
                if (userCards.length === 0) {
                  setError('Для создания сделки необходимо добавить банковскую карту');
                  return;
                }
                setShowCreateDealModal(true);
              }}
              disabled={userCards.length === 0}
            >
              <span className="text-2xl">+</span>
              <span>Создать сделку</span>
            </button>
          </div>

          {/* Right Column - Deals */}
          <div className="xl:col-span-2">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6">
              {/* Deals Filter Tabs */}
              <div className="flex items-center space-x-12 mb-8">
                <div 
                  className={`text-2xl cursor-pointer transition-colors ${
                    activeFilter === 'active' ? 'text-white' : 'text-white/50'
                  }`}
                  onClick={() => setActiveFilter('active')}
                >
                  Активные сделки
                </div>
                <div 
                  className={`text-2xl cursor-pointer transition-colors ${
                    activeFilter === 'history' ? 'text-white' : 'text-white/50'
                  }`}
                  onClick={() => setActiveFilter('history')}
                >
                  История
                </div>
              </div>
              
              {dealsLoading ? (
                <div className="flex justify-center py-12">
                  <Spinner size="lg" className="text-white" />
                </div>
              ) : deals.length === 0 ? (
                <div className="text-center py-12 text-white/70">
                  У вас пока нет сделок
                </div>
              ) : (
                <>
                  {/* Показываем отфильтрованные сделки */}
                  {getFilteredDeals().length === 0 ? (
                    <div className="text-center py-12 text-white/70">
                      {activeFilter === 'active' ? 'У вас нет активных сделок' : 'История сделок пуста'}
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {getFilteredDeals().map((deal) => (
                        <div 
                          key={deal.id} 
                          className="flex items-start justify-between cursor-pointer hover:bg-white/5 rounded-2xl p-4 transition-colors"
                          onClick={() => handleDealClick(deal)}
                        >
                          {/* Left side - Share icon */}
                          <div 
                            className="w-12 h-12 bg-[#2e3036] rounded-full flex items-center justify-center mr-4 cursor-pointer hover:bg-[#2e3036]/80 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareDeal(deal);
                            }}
                          >
                            <ShareIcon className="w-6 h-6 text-white" />
                          </div>
                         
                          {/* Center - Deal details */}
                          <div className="flex-1 min-w-0">
                            {/* Deal name with truncation */}
                            <div className="text-white text-lg mb-2 truncate">
                              {deal.purpose}
                            </div>
                            
                            {/* Creation date */}
                            <div className="text-white/70 text-base">
                              {formatDate(deal.createdAt)}
                            </div>
                          </div>
                          
                          {/* Right side - Amount and Status */}
                          <div className="text-right ml-4 flex-shrink-0">
                            {/* Amount */}
                            <div className="text-white text-2xl mb-3">
                              {formatAmount(deal.amount)} ₽
                            </div>
                            
                            {/* Status chip */}
                            <Chip
                              color={getStatusColor(deal.status)}
                              variant="flat"
                              size="sm"
                              className={`${
                                deal.status === DealStatus.Finished 
                                  ? 'bg-green-500/20 text-green-300 border-green-400/30 text-sm px-3 py-2' 
                                  : 'bg-white/20 text-white border-white/30 text-sm px-3 py-2'
                              }`}
                            >
                              {getStatusText(deal.status)}
                            </Chip>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Deal Modal */}
      <CreateDealModal
        isOpen={showCreateDealModal}
        onClose={() => setShowCreateDealModal(false)}
        onSubmit={handleCreateDeal}
        isLoading={createDealLoading}
        userCards={userCards}
      />

      {/* Profile Status Modal */}
      <ProfileStatusModal
        isOpen={showProfileStatusModal}
        onClose={() => setShowProfileStatusModal(false)}
        user={user || undefined}
        onUserUpdate={updateUser}
      />

      {/* Card Management Modal */}
      {userCards.length > 0 && (
        <CardManagementModal
          isOpen={showCardManagementModal}
          onClose={() => setShowCardManagementModal(false)}
          onReplaceCard={handleReplaceCard}
          onDeleteCard={handleDeleteCard}
          cardInfo={{
            id: userCards[0].id,
            maskedCardNumber: userCards[0].maskedCardNumber
          }}
        />
      )}

      {/* Deal Details Modal */}
      <DealDetailsModal
        isOpen={showDealDetailsModal}
        onClose={handleCloseDealDetails}
        deal={selectedDeal}
        onDealUpdate={async () => {
          try {
            const updatedDeals = await dealService.getDeals();
            setDeals(updatedDeals);
          } catch (error) {
            console.error('Failed to refresh deals:', error);
          }
        }}
      />

      {/* Notification Panel */}
      {notification.isVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setNotification(prev => ({ ...prev, isVisible: false }))}
          />
          
          {/* Notification Panel */}
          <div className="relative bg-[#141826] rounded-3xl w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="text-white text-left p-6 border-b border-white/10">
              <button
                onClick={() => setNotification(prev => ({ ...prev, isVisible: false }))}
                className="absolute right-6 top-6 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
              >
                ✕
              </button>
              <h3 className="text-2xl">Уведомление</h3>
            </div>
            
            {/* Body */}
            <div className="text-white p-6">
              <div className="text-center py-6">
                <div className="text-white text-lg mb-6">
                  {notification.message}
                </div>
                
                <button
                  onClick={() => setNotification(prev => ({ ...prev, isVisible: false }))}
                  className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95"
                >
                  <span>Ок</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopDashboard;
